// apps/web/src/features/calendar/group/duNormalize.ts
type AnyObj = Record<string, any>;

const DAY_TO_INDEX: Record<string, number> = { d1: 0, d2: 1, d3: 2, d4: 3, d5: 4, d6: 5 };

const SLOT_TO_TIME: Record<string, { start: string; end: string }> = {
  a: { start: "08:00", end: "08:50" },
  b: { start: "09:00", end: "09:50" },
  c: { start: "10:00", end: "10:50" },
  d: { start: "11:00", end: "11:50" },
  e: { start: "12:00", end: "12:50" },
  f: { start: "13:05", end: "13:55" },
  g: { start: "14:00", end: "14:50" },
  h: { start: "15:00", end: "15:50" },
  i: { start: "16:00", end: "16:50" },
  j: { start: "17:00", end: "17:50" },
  k: { start: "18:00", end: "18:50" },
  l: { start: "19:00", end: "19:50" },
  m: { start: "20:00", end: "20:50" },
  n: { start: "21:00", end: "21:50" },
};

function pickStr(v: any) {
  return v == null ? "" : String(v).trim();
}

function parseDateAny(v: any): Date | null {
  const s = pickStr(v);
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfWeekMondayLocal(now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const jsDay = d.getDay(); // 0=Sun..6=Sat
  const mondayOffset = (jsDay + 6) % 7;
  d.setDate(d.getDate() - mondayOffset);
  return d;
}

function setHHMM(base: Date, hhmm: string) {
  const [hh, mm] = hhmm.split(":").map((x) => parseInt(x, 10));
  const d = new Date(base);
  d.setHours(hh || 0, mm || 0, 0, 0);
  return d;
}

function extractLessons(duJson: any): AnyObj[] {
  if (!duJson) return [];
  if (Array.isArray(duJson)) return duJson as AnyObj[];
  if (Array.isArray(duJson.body)) return duJson.body as AnyObj[];
  if (Array.isArray(duJson.lessons)) return duJson.lessons as AnyObj[];
  if (Array.isArray(duJson.items)) return duJson.items as AnyObj[];
  return [];
}

/**
 * Нормализация DU:
 * - если есть startDateTime/endDateTime → используем
 * - иначе конвертим ClasstimeDay(d1..d6) + ClasstimeTime(a..n) в даты текущей недели
 * - добавляем startTime "HH:MM" для mergeRooms.ts
 */
export function normalizeDuLessons(duJson: any, now = new Date()) {
  const lessons = extractLessons(duJson);
  const monday = startOfWeekMondayLocal(now);

  return lessons.map((l) => {
    const subjectName =
      pickStr(l?.subjectName) ||
      pickStr(l?.subject) ||
      pickStr(l?.disciplineName) ||
      pickStr(l?.name) ||
      "Lesson";

    const room =
      pickStr(l?.room) ||
      pickStr(l?.auditorium) ||
      pickStr(l?.classroom) ||
      pickStr(l?.cabinet) ||
      "";

    const lessonType =
      pickStr(l?.lessonType) ||
      pickStr(l?.lesson_type) ||
      pickStr(l?.lessonTypeName) ||
      "";

    let start =
      parseDateAny(l?.startDateTime) ||
      parseDateAny(l?.start) ||
      parseDateAny(l?.startAt) ||
      parseDateAny(l?.beginDateTime);

    let end =
      parseDateAny(l?.endDateTime) ||
      parseDateAny(l?.end) ||
      parseDateAny(l?.endAt) ||
      parseDateAny(l?.finishDateTime);

    const dayCode = pickStr(l?.classtimeDay || l?.ClasstimeDay || l?.classtime_day).toLowerCase(); // d1..d6
    const slotCode = pickStr(l?.classtimeTime || l?.ClasstimeTime || l?.classtime_time).toLowerCase(); // a..n
    const dayIndex = DAY_TO_INDEX[dayCode];
    const slot = SLOT_TO_TIME[slotCode];

    if (!start && dayIndex != null && slot) {
      const dayBase = new Date(monday);
      dayBase.setDate(monday.getDate() + dayIndex);
      start = setHHMM(dayBase, slot.start);
      end = setHHMM(dayBase, slot.end);
    }

    const startTime =
      slot?.start ||
      (start
        ? `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`
        : "");

    return {
      ...l,
      subjectName,
      room,
      startDateTime: start ? start.toISOString() : l?.startDateTime,
      endDateTime: end ? end.toISOString() : l?.endDateTime,
      startTime,
      lessonType,
    };
  });
}
